import React from 'react';
import { Image } from '../../../commonTypes/image';
import { CallToAction } from '../../../commonTypes/links';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme } from '../../../theme';
import Background, { BackgroundType } from '../../Background';
import FeatureContainer from '../FeatureContainer';
import FeatureContents from '../FeatureContents';

export type FeatureProps = {
  title?: string;
  subtitle?: string;
  body: string;
  cta?: CallToAction;
  shouldUsePrimaryCta?: boolean;
  secondaryCta?: CallToAction;
  image?: Image;
  customBulletIcon?: string;
  invertContent?: boolean;
  invertContentOnMobile?: boolean;
  renderImageAsLink?: boolean;
  displayBackground?: boolean;
  backgroundType?: BackgroundType;
  children?: React.ReactNode;
} & ComponentProps;

/**
 * @param invertContent - Invert content on landscape tablets, desktops, and portrait tablets.
 *                        This swaps the position of the text and the image. E.g. if set to true, images will be to the right of the text instead of the left
 * @param invertContentOnMobile - This swaps the position of the text and the image on mobile. E.g. if set to true,
 *                                images will be below the text instead of above
 */
const Feature: React.FC<FeatureProps> = ({
  title,
  subtitle,
  body,
  cta,
  secondaryCta,
  image,
  customBulletIcon,
  invertContent,
  invertContentOnMobile = false,
  renderImageAsLink = true,
  shouldUsePrimaryCta = false,
  displayBackground,
  backgroundType = BackgroundType.SOLID,
  children,
  ...props
}) => {
  const background = !displayBackground ? BackgroundType.NONE : backgroundType;

  return (
    <Background backgroundType={background}>
      <FeatureContainer
        cta={cta}
        image={image}
        invertContent={invertContent}
        invertContentOnMobile={invertContentOnMobile}
        renderImageAsLink={renderImageAsLink}
        {...componentProps(props)}>
        <FeatureContents
          title={title}
          subtitle={subtitle}
          body={body}
          cta={cta}
          secondaryCta={secondaryCta}
          customBulletIcon={customBulletIcon}
          // We have this spacing even when there's no image for backwards styling consistency
          addImageToHeadingSpacingOnMobile={!invertContentOnMobile}
          shouldUsePrimaryCta={shouldUsePrimaryCta}
        />
      </FeatureContainer>
      {children}
    </Background>
  );
};

export default applyComponentTheme(Feature, 'Feature');
